import { cn } from '@/lib/cn'

/**
 * The NinjaDEX mark.
 *
 * An N drawn as a single routed line: capital enters at the lower left, crosses the diagonal,
 * and leaves through the brass node at the top right. The protocol's own diagram, set as a
 * monogram — see docs/brand-mark.html for the specimen sheet and the constraints.
 *
 * Inline SVG rather than an `<img src="/brand/mark.svg">` on purpose. The stroke takes
 * `currentColor`, so one component serves the enamel rail, a reversed dark surface and a
 * one-colour print without three files and three code paths deciding between them.
 *
 * The round caps are load-bearing: they are what makes this a length of pipe rather than a
 * letterform with soft corners. Do not square them.
 */
/**
 * The mark's path, and the exit terminal that sits at the end of it.
 *
 * Exported because the loading indicator draws this same figure — see ui/loading-indicator.tsx,
 * where the pipe is stroked on progressively to show capital moving through the route. Two
 * copies of this data would drift the first time the monogram is retuned, and the drift would
 * be invisible: a logo and a loader that disagree by two units still each look deliberate.
 *
 * `BrandTile` below does NOT use these. Its path is inset for the enclosing tile, which is a
 * genuinely different figure rather than a copy that fell behind.
 */
export const MARK_PATH_D = 'M16 49 L16 16 L48 49 L48 16'
export const MARK_NODE = { cx: 48, cy: 16, r: 5.5 } as const

export function BrandMark({
  className,
  /** Brass by default — it marks the exit terminal, and brass means reward. */
  node = 'var(--color-brass)',
}: {
  className?: string
  node?: string
}) {
  return (
    <svg viewBox="0 0 64 64" fill="none" aria-hidden className={className}>
      <path
        d={MARK_PATH_D}
        stroke="currentColor"
        strokeWidth="11"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={MARK_NODE.cx} cy={MARK_NODE.cy} r={MARK_NODE.r} fill={node} />
    </svg>
  )
}

/**
 * The mark on an enamel tile — the app-icon treatment, used wherever the mark needs to sit on
 * its own ground rather than inherit one.
 *
 * The stroke is 10 here against the free-standing mark's 11. That is not an inconsistency:
 * the same weight optically thickens once it is enclosed by a filled shape, so matching the
 * numbers would have made the tile read heavier than the logo it is supposed to be.
 */
export function BrandTile({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" aria-hidden className={cn('overflow-hidden', className)}>
      {/*
       * Painted from TOKENS, not literals.
       *
       * These were four hardcoded hex values carried over from the light theme, and on dark
       * they were the only place in the running app still showing them: the gold dot rendered
       * #c2a053 while every other gold in the product is `--color-brass`, so the logo's
       * accent was a colour that existed nowhere else on screen. A palette audit is how that
       * surfaced — it is invisible by eye, because two golds a few percent apart just look
       * like one gold you are not looking at closely.
       *
       * `--m-enamel` is a gradient on light and a flat fill on dark, and neither can be a
       * `stopColor`, so the tile takes the fill directly rather than through a <linearGradient>.
       */}
      <rect width="64" height="64" rx="15" style={{ fill: 'var(--color-accent-deep, var(--color-accent))' }} />
      <path
        d="M18 47 L18 19 L46 47 L46 19"
        style={{ stroke: 'var(--color-on-enamel)' }}
        strokeWidth="10"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <circle cx="46" cy="19" r="5" style={{ fill: 'var(--color-brass)' }} />
    </svg>
  )
}
